import type { AppContext, ViewCleanup } from "../app";
import {
  loadCountries,
  groupByRegion,
  type CountryMetadata,
} from "../data/countries";
import { loadState, saveState } from "../state/storage";
import { recordCorrect, recordWrong } from "../state/stats";
import { getArcadeBest, recordArcadeScore } from "../state/arcade";
import { pushLeaderboardBests } from "../state/sync";
import {
  startRound,
  drawNext,
  returnMissed,
  type RoundState,
} from "../lib/selection";
import { getCountriesForRegion } from "./region-picker";

const ROUNDS = 10;
const REVEAL_MS = 1400;

export const mountArcadeReverse = async (
  root: HTMLElement,
  ctx: AppContext,
): Promise<ViewCleanup> => {
  const region = root.dataset.region;
  if (!region) {
    ctx.nav.goArcade();
    return () => {};
  }

  root.classList.add("view-arcade-reverse");

  const table = await loadCountries();
  const grouped = groupByRegion(table);
  let appState = loadState();
  const pool = getCountriesForRegion(region, grouped, appState);
  const poolIsos = pool.map((c) => c.iso);

  root.innerHTML = `
    <button type="button" class="arcade-run-back" aria-label="Back">← Back</button>
    <div class="arcade-run-hud">
      <span class="arcade-reverse-round">1</span> / ${ROUNDS}
      <span class="arcade-run-divider">·</span>
      <span class="arcade-run-correct">0</span> ✓
    </div>
    <section class="arcade-reverse-prompt">
      <p class="arcade-reverse-label">Find this country</p>
      <p class="arcade-reverse-name">…</p>
      <p class="arcade-reverse-hint">Tap a country on the globe.</p>
      <p class="arcade-reverse-feedback" hidden></p>
    </section>
    <section class="arcade-run-end" hidden>
      <h1 class="arcade-run-final-title">Run complete</h1>
      <p class="arcade-run-final-score"></p>
      <p class="arcade-run-final-best"></p>
      <div class="arcade-run-actions">
        <button type="button" class="arcade-run-retry">Run it again</button>
        <button type="button" class="arcade-run-leaderboard">🏆 Leaderboard</button>
        <button type="button" class="arcade-run-exit">Back to Arcade</button>
      </div>
    </section>
  `;

  const backBtn = root.querySelector(".arcade-run-back") as HTMLButtonElement;
  const roundEl = root.querySelector(".arcade-reverse-round") as HTMLSpanElement;
  const correctEl = root.querySelector(".arcade-run-correct") as HTMLSpanElement;
  const promptCard = root.querySelector(".arcade-reverse-prompt") as HTMLElement;
  const nameEl = root.querySelector(".arcade-reverse-name") as HTMLParagraphElement;
  const hintEl = root.querySelector(".arcade-reverse-hint") as HTMLParagraphElement;
  const feedbackEl = root.querySelector(".arcade-reverse-feedback") as HTMLParagraphElement;
  const endEl = root.querySelector(".arcade-run-end") as HTMLElement;
  const finalScore = root.querySelector(".arcade-run-final-score") as HTMLParagraphElement;
  const finalBest = root.querySelector(".arcade-run-final-best") as HTMLParagraphElement;
  const retryBtn = root.querySelector(".arcade-run-retry") as HTMLButtonElement;
  const leaderboardBtn = root.querySelector(".arcade-run-leaderboard") as HTMLButtonElement;
  const exitBtn = root.querySelector(".arcade-run-exit") as HTMLButtonElement;

  let current: CountryMetadata | null = null;
  let round = 0;
  let correctCount = 0;
  let destroyed = false;
  let ended = false;
  let revealTimer: number | null = null;
  let awaiting = false;
  let roundState: RoundState = startRound(poolIsos, Math.random);

  if (pool.length === 0) {
    nameEl.textContent = `"${region}" has no countries.`;
    hintEl.hidden = true;
    return () => {
      ctx.globe.setClickHandler(null);
      ctx.globe.setHoverEnabled(true);
      root.classList.remove("view-arcade-reverse");
    };
  }

  // The globe must be tappable in reverse mode.
  ctx.globe.setHoverEnabled(true);
  ctx.globe.setTargetIso(null);
  ctx.globe.setGuessIso(null);

  const endRun = (): void => {
    if (ended) return;
    ended = true;
    if (revealTimer !== null) {
      window.clearTimeout(revealTimer);
      revealTimer = null;
    }
    promptCard.hidden = true;
    endEl.hidden = false;
    finalScore.textContent = `${correctCount} / ${ROUNDS} correct`;
    const prevBest = getArcadeBest(appState, "reverse", region);
    const result = recordArcadeScore(
      appState,
      "reverse",
      region,
      correctCount,
      Date.now(),
    );
    appState = result.state;
    saveState(appState);
    if (result.isNewBest) {
      finalBest.innerHTML = `<strong>New best!</strong> (was ${prevBest ? prevBest.score : 0} / ${ROUNDS})`;
      void pushLeaderboardBests(appState);
    } else {
      finalBest.textContent = `Best: ${prevBest ? prevBest.score : 0} / ${ROUNDS}`;
    }
    ctx.globe.setClickHandler(null);
    ctx.globe.setTargetIso(null);
    ctx.globe.setGuessIso(null);
  };

  const nextRound = (): void => {
    if (destroyed || ended) return;
    if (round >= ROUNDS) {
      endRun();
      return;
    }
    round += 1;
    roundEl.textContent = String(round);
    const draw = drawNext(roundState, Math.random);
    roundState = draw.state;
    const pickedIso = draw.iso;
    if (!pickedIso) {
      endRun();
      return;
    }
    const picked = table[pickedIso];
    if (!picked) return;
    current = picked;
    nameEl.textContent = picked.canonicalName;
    feedbackEl.hidden = true;
    feedbackEl.textContent = "";
    feedbackEl.className = "arcade-reverse-feedback";
    hintEl.hidden = false;
    awaiting = true;
    // No target highlight in reverse — the player has to find it.
    ctx.globe.setTargetIso(null);
    ctx.globe.setGuessIso(null);
  };

  const handleTap = (iso: string | null): void => {
    if (destroyed || ended || !awaiting || !current) return;
    if (iso === null) return; // tapped open ocean — let them try again
    awaiting = false;
    const correct = iso === current.iso;
    const now = Date.now();
    if (correct) {
      correctCount += 1;
      correctEl.textContent = String(correctCount);
      appState = recordCorrect(appState, current.iso, now);
      ctx.globe.setTargetIso(current.iso);
      feedbackEl.hidden = false;
      feedbackEl.className = "arcade-reverse-feedback is-correct";
      feedbackEl.innerHTML = `<strong>✓ ${current.canonicalName}</strong>`;
    } else {
      appState = recordWrong(appState, current.iso, now);
      roundState = returnMissed(roundState, current.iso, Math.random);
      // Highlight the right answer in amber + the user's guess in red.
      ctx.globe.setTargetIso(current.iso);
      ctx.globe.setGuessIso(iso);
      const guessed = table[iso];
      feedbackEl.hidden = false;
      feedbackEl.className = "arcade-reverse-feedback is-wrong";
      feedbackEl.innerHTML = `<strong>✗ ${current.canonicalName}</strong><br/><small>You tapped ${guessed ? guessed.canonicalName : "open ocean"}.</small>`;
    }
    saveState(appState);
    void ctx.globe.flyToIso(current.iso, { durationMs: 700 });
    hintEl.hidden = true;
    revealTimer = window.setTimeout(() => {
      revealTimer = null;
      nextRound();
    }, REVEAL_MS);
  };
  ctx.globe.setClickHandler(handleTap);

  const onBack = (): void => ctx.nav.goArcade();
  const onRetry = (): void => ctx.nav.goArcadeMode({ mode: "reverse", region });
  const onExit = (): void => ctx.nav.goArcade();
  const onLeaderboard = (): void =>
    ctx.nav.goLeaderboard({ mode: "reverse", region });
  backBtn.addEventListener("click", onBack);
  retryBtn.addEventListener("click", onRetry);
  leaderboardBtn.addEventListener("click", onLeaderboard);
  exitBtn.addEventListener("click", onExit);

  nextRound();

  return () => {
    destroyed = true;
    if (revealTimer !== null) window.clearTimeout(revealTimer);
    backBtn.removeEventListener("click", onBack);
    retryBtn.removeEventListener("click", onRetry);
    leaderboardBtn.removeEventListener("click", onLeaderboard);
    exitBtn.removeEventListener("click", onExit);
    ctx.globe.setClickHandler(null);
    ctx.globe.setTargetIso(null);
    ctx.globe.setGuessIso(null);
    root.classList.remove("view-arcade-reverse");
  };
};
